import { useMemo, useState } from 'react'
import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import styled from 'styled-components'
import { ArrowLeft, ArrowRight } from 'svgIcons'

type TProps = {
  type?: 'query' | 'value'
  className?: string
  totalCount?: number
  pageSize?: number
  stepSize?: number
  value?: number
  onChange?: (page: number) => void
  isDisabled?: boolean
  scrollTop?: boolean
}

const Pagination: React.FC<TProps> = ({
  type = 'query',
  className,
  totalCount = 1,
  pageSize = 1,
  stepSize = 10,
  onChange,
  value = 1,
  isDisabled = false,
  scrollTop = false,
}) => {
  const router = useRouter()
  const [pageState, setPageState] = useState(1)

  const currentPage = useMemo(() => {
    if (type === 'value') {
      return pageState
    } else {
      return router.query.page ? +router.query.page : 1
    }
  }, [pageState, router.query.page])
  const [step, setStep] = useState(1)

  React.useEffect(() => {
    setPageState(value)
    setStep(Math.ceil(value / stepSize))
  }, [value])

  const totalPage = Math.ceil(totalCount / pageSize)

  const stepArr = useMemo(() => {
    const firstStepNumber = step * stepSize - stepSize + 1
    const stepArr: number[] = []
    for (let i = 0; i < stepSize; i++) {
      if (firstStepNumber + i <= totalPage) stepArr.push(firstStepNumber + i)
    }
    return stepArr
  }, [step, stepSize, totalPage])

  const addPage = (page: number) => {
    if (type === 'value') {
      setPageState(page)
    }

    onChange && onChange(page)
  }

  const handleNextBtn = () => {
    setStep((prev) => prev + 1)
    addPage(step * stepSize + 1)
  }

  const handlePrevBtn = () => {
    setStep((prev) => prev - 1)
    addPage((step - 1) * stepSize - stepSize + 1)
  }

  const hasPrevStep = stepArr[0] > 1
  const hasNextStep = stepArr[stepSize - 1] < totalPage

  if (totalCount === 0) return null

  return (
    <StyledWrapper className={className} isDisabled={isDisabled}>
      <button
        className="page-btn"
        disabled={!hasPrevStep}
        onClick={handlePrevBtn}
      >
        <ArrowLeft />
      </button>
      <div className="page-list">
        {stepArr.map((page) => {
          return type === 'value' ? (
            <button
              key={page}
              className="page"
              data-active={page === currentPage}
              onClick={() => addPage(page)}
            >
              {page}
            </button>
          ) : (
            <Link
              key={page}
              href={{
                query: {
                  ...router.query,
                  page,
                },
              }}
              scroll={scrollTop}
            >
              <a
                className="page"
                data-active={page === currentPage}
                onClick={() => addPage(page)}
              >
                {page}
              </a>
            </Link>
          )
        })}
      </div>
      <button
        className="page-btn"
        disabled={!hasNextStep}
        onClick={handleNextBtn}
      >
        <ArrowRight />
      </button>
    </StyledWrapper>
  )
}

export default Pagination

type TStyledProps = {
  isDisabled: boolean
}

const StyledWrapper = styled.div<TStyledProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};

  .page-btn {
    cursor: pointer;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #eee;
    border: none;
    border-radius: 5px;
    color: black;
    svg {
      fill: black;
      width: 16px;
      height: 16px;
    }
    &:disabled {
      cursor: default;
      color: white;
      svg {
        fill: white;
      }
    }
  }
  .page-list {
    display: flex;
    & > .page {
      cursor: pointer;
      border: none;
      background: none;
      padding: 10px;
      color: #bbbbbb;
      &[data-active='true'] {
        color: #01a0ff;
      }
    }
  }
`
